import { Injectable } from '@angular/core';

import { CurrencyConfig } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { ISelectionProvider, NumpadOperation, NumpadState } from './model';
import { NumpadService } from './numpad.service';
import NumpadActions from './store/actions';

@Injectable()
export class NumpadInputConnector implements ISelectionProvider {
    private focused: boolean;
    private timeout?: () => void;
    private stateChangeSub?: Subscription;
    input: HTMLInputElement;

    constructor(
        private numpadService: NumpadService,
        private timerService: TimerService,
        private currencyConfig: CurrencyConfig,
        private store: Store,
    ) {}

    init(input: HTMLInputElement) {
        this.input = input;
        this.numpadService.registerSelectionProvider(this);
        this.stateChangeSub = this.numpadService.state$.subscribe((state) => this.stateChange(state));
    }

    deInit() {
        this.cleanTimeout();
        this.stateChangeSub?.unsubscribe();
    }

    getSelection(currentValue: string): { start: number; end: number } {
        const length = currentValue.length;
        if (!this.focused) {
            return {
                start: length,
                end: length,
            };
        }

        return {
            start: this.input.selectionStart !== null ? this.input.selectionStart : length,
            end: this.input.selectionEnd !== null ? this.input.selectionEnd : length,
        };
    }

    clearFocus(): void {
        this.timeout = this.timerService.setTimeoutOutsideAngular(() => {
            this.focused = false;
            this.store.dispatch(NumpadActions.numpadFocus({ numpadFocus: false }));
        }, 200);
    }

    focus(): void {
        this.cleanTimeout();
        this.focused = true;
        this.store.dispatch(NumpadActions.numpadFocus({ numpadFocus: true }));

        this.numpadService.open();
    }

    private cleanTimeout(): void {
        if (!this.timeout) {
            return;
        }
        this.timeout();
        delete this.timeout;
    }

    private stateChange(state: NumpadState): void {
        this.input.value = state.stake.isEmpty ? '' : this.currencyConfig.showStakeCurrency ? state.stake.currencyFormatted : state.stake.formatted;

        if (state.operation === NumpadOperation.Open) {
            this.input.select();
            this.focusInput();
        }

        if (this.focused && state.isOpened && isDefined(state.selectionStart)) {
            this.focusInput(state.selectionStart);
        }
    }

    private focusInput(position?: number): void {
        this.timerService.setTimeoutOutsideAngular(() => {
            if (position) {
                this.input.setSelectionRange(position, position);
            }

            this.input.focus();
        });
    }
}
